import { Action, parsePath, Location } from 'history';
import {
	createActionsHook,
	createContainer,
	createHook,
	createStore,
	defaultRegistry,
	batch,
	BoundActions,
	HookActionsFunction,
	HookFunction,
	OverrideContainerComponent,
	Store,
	StoreInstance,
} from 'react-sweet-state';

import {
	DEFAULT_ACTION,
	DEFAULT_HISTORY,
	DEFAULT_MATCH,
	DEFAULT_ROUTE,
} from '../../common/constants';
import { Query } from '../../common/types';
import {
	findRouterContext,
	isServerEnvironment,
	generatePath as generatePathUsingPathParams,
	generateLocationFromPath,
	warmupMatchRouteCache,
} from '../../common/utils';

import { AllRouterActions, ContainerProps, EntireRouterState, RouterState } from './types';
import {
	getRelativePath,
	isExternalAbsolutePath,
	updateQueryParams,
	getRelativeURLFromLocation,
	shouldReload,
} from './utils';

export const INITIAL_STATE: EntireRouterState = {
	action: DEFAULT_ACTION,
	basePath: '',
	location: DEFAULT_HISTORY.location,
	history: DEFAULT_HISTORY,
	match: DEFAULT_MATCH,
	onPrefetch: undefined,
	query: {},
	route: DEFAULT_ROUTE,
	routes: [],
	unlisten: null,
	plugins: [],
};

const actions: AllRouterActions = {
	/**
	 * Bootstraps the store with initial data.
	 *
	 */
	bootstrapStore:
		(props) =>
		({ setState, dispatch }) => {
			const { basePath = '', history, initialRoute, onPrefetch, routes, plugins } = props;
			const routerContext = findRouterContext(initialRoute ? [initialRoute] : routes, {
				location: history.location,
				basePath,
			});

			setState({
				...routerContext,
				basePath,
				history,
				onPrefetch,
				routes,
				location: history.location,
				action: history.action,
				plugins,
			});

			if (!isServerEnvironment()) {
				dispatch(actions.listen());
			}
		},

	/**
	 * Starts listening to browser history and sets the unlisten function in state.
	 * Will request route resources on route change.
	 *
	 */
	listen:
		() =>
		({ getState, setState }) => {
			const { history, unlisten } = getState();
			if (unlisten) unlisten();

			type LocationUpateV4 = [Location, Action];
			type LocationUpateV5 = [{ location: Location; action: Action }];

			const stopListening = history.listen((...update: LocationUpateV4 | LocationUpateV5) => {
				const location = update.length === 2 ? update[0] : update[0].location;
				const action = update.length === 2 ? update[1] : update[0].action;

				const {
					plugins,
					routes,
					basePath,
					match: currentMatch,
					route: currentRoute,
					query: currentQuery,
				} = getState();

				const nextContext = findRouterContext(routes, {
					location,
					basePath,
				});

				const prevContext = {
					route: currentRoute,
					match: currentMatch,
					query: currentQuery,
				};

				const shouldReloadByPlugin = new Map(
					plugins.map((plugin) => [
						plugin.id,
						shouldReload({
							context: nextContext,
							prevContext,
							pluginId: plugin.id,
						}),
					]),
				);

				/* Explicitly batch update
				 * as we need resources cleaned + route changed + resource fetch started together
				 * If we do not batch, React might be re-render when route changes but resource
				 * fetching has not started yet, making the app render with data null */

				batch(() => {
					plugins.forEach((p) => {
						if (shouldReloadByPlugin.get(p.id)) {
							p.beforeRouteLoad?.({
								context: prevContext,
								nextContext,
							});
						}
					});

					setState({
						...nextContext,
						location,
						action,
					});

					plugins.forEach((p) => {
						if (shouldReloadByPlugin.get(p.id)) {
							p.routeLoad?.({ context: nextContext, prevContext });
						}
					});
				});
			});

			setState({
				unlisten: stopListening,
			});
		},

	push:
		(path, state) =>
		({ getState }) => {
			const { history, basePath } = getState();
			if (isExternalAbsolutePath(path)) {
				window.location.assign(path as string);
			} else {
				history.push(getRelativePath(path, basePath), state);
			}
		},

	pushTo:
		(route, attributes = {}) =>
		({ getState }) => {
			const { history, basePath } = getState();
			const location = generateLocationFromPath(route.path, {
				...attributes,
				basePath,
			});
			warmupMatchRouteCache(route, location.pathname, attributes.query, basePath);
			history.push(location as any, attributes?.state);
		},

	replace:
		(path, state) =>
		({ getState }) => {
			const { history, basePath } = getState();
			if (isExternalAbsolutePath(path)) {
				window.location.replace(path as string);
			} else {
				history.replace(getRelativePath(path, basePath) as any, state);
			}
		},

	replaceTo:
		(route, attributes = {}) =>
		({ getState }) => {
			const { history, basePath } = getState();
			const location = generateLocationFromPath(route.path, {
				...attributes,
				basePath,
			});
			warmupMatchRouteCache(route, location.pathname, attributes.query, basePath);
			history.replace(location as any, attributes?.state);
		},

	goBack:
		() =>
		({ getState }) => {
			const { history } = getState();

			// history@4 uses goBack(), history@5 uses back()
			if ('goBack' in history) {
				history.goBack();
			} else if ('back' in history) {
				history.back();
			} else {
				throw new Error('History does not support goBack');
			}
		},

	goForward:
		() =>
		({ getState }) => {
			const { history } = getState();

			// history@4 uses goForward(), history@5 uses forward()
			if ('goForward' in history) {
				history.goForward();
			} else if ('forward' in history) {
				history.forward();
			} else {
				throw new Error('History does not support goForward');
			}
		},

	registerBlock:
		(blocker) =>
		({ getState }) => {
			const { history } = getState();

			return history.block(blocker);
		},

	getContext:
		() =>
		({ getState }) => {
			const { query, route, match } = getState();

			return { query, route, match };
		},

	getBasePath:
		() =>
		({ getState }) => {
			const { basePath } = getState();

			return basePath;
		},

	updateQueryParam:
		(params, updateType = 'push') =>
		({ getState }) => {
			const { query: existingQueryParams, history, location } = getState();
			const updatedQueryParams = { ...existingQueryParams, ...params };
			// remove undefined keys
			Object.keys(updatedQueryParams).forEach(
				(key) => updatedQueryParams[key] === undefined && delete updatedQueryParams[key],
			);
			const existingPath = updateQueryParams(location, existingQueryParams);
			const updatedPath = updateQueryParams(location, updatedQueryParams as Query);

			if (updatedPath !== existingPath) {
				history[updateType](updatedPath);
			}
		},

	updatePathParam:
		(params, updateType = 'push') =>
		({ getState }) => {
			const {
				history,
				location,
				route: { path },
				match: { params: existingPathParams },
				basePath,
			} = getState();
			const pathWithBasePath = basePath + path;
			const updatedPathParams = { ...existingPathParams, ...params };
			const updatedPath = generatePathUsingPathParams(pathWithBasePath, updatedPathParams);
			const updatedLocation = { ...location, pathname: updatedPath };

			const existingRelativePath = getRelativeURLFromLocation(location);
			const updatedRelativePath = getRelativeURLFromLocation(updatedLocation);

			if (updatedRelativePath !== existingRelativePath) {
				history[updateType](updatedRelativePath);
			}
		},
	loadPlugins:
		() =>
		({ getState }) => {
			const { plugins, match, query, route } = getState();

			plugins.forEach((p) => p.routeLoad?.({ context: { match, query, route } }));
		},
	prefetchRoute:
		(path, nextContext) =>
		({ getState }) => {
			const { plugins, routes, basePath, onPrefetch } = getState();
			const { route, match, query } = getRouterState();

			let resolvedContext = nextContext;
			if (!resolvedContext && !isExternalAbsolutePath(path)) {
				const location = parsePath(getRelativePath(path, basePath) as any);
				resolvedContext = findRouterContext(routes, { location, basePath });
			}

			if (resolvedContext == null) return;
			const nextLocationContext = resolvedContext;

			batch(() => {
				plugins.forEach((p) =>
					p.routePrefetch?.({
						context: { route, match, query },
						nextContext: nextLocationContext,
					}),
				);
				if (onPrefetch) onPrefetch(nextLocationContext);
			});
		},
};

type State = EntireRouterState;

type Actions = AllRouterActions;

export const RouterStore: Store<EntireRouterState, AllRouterActions> = createStore<State, Actions>({
	initialState: INITIAL_STATE,
	actions,
	name: 'router',
});

export const RouterContainer: OverrideContainerComponent<ContainerProps> = createContainer<
	State,
	Actions,
	ContainerProps
>(RouterStore, {
	displayName: 'RouterContainer',
	onInit:
		() =>
		({ dispatch }, props) => {
			dispatch(actions.bootstrapStore(props));
			if (!isServerEnvironment()) dispatch(actions.loadPlugins());
		},
	onCleanup: () => (state) => {
		if (process.env.NODE_ENV === 'development') {
			// eslint-disable-next-line no-console
			console.warn(
				`Warning: react-resource-router has been unmounted! Was this intentional? Resources will be refetched when the router is mounted again.`,
			);
		}
		if (!isServerEnvironment()) {
			const { unlisten } = state.getState();
			if (unlisten) unlisten();
		}
	},
});

export const useRouterStore: HookFunction<
	EntireRouterState,
	BoundActions<EntireRouterState, AllRouterActions>,
	void
> = createHook<EntireRouterState, AllRouterActions>(RouterStore);

export const useRouterStoreActions: HookActionsFunction<
	BoundActions<EntireRouterState, AllRouterActions>
> = createActionsHook<EntireRouterState, AllRouterActions>(RouterStore);

/**
 * Utility to create custom hooks without re-rendering on route change
 */
export function createRouterSelector<T, U = void>(selector: (state: RouterState, props: U) => T) {
	const useHook = createHook<EntireRouterState, AllRouterActions, T, U>(RouterStore, { selector });

	return function useRouterSelector(...args: U extends undefined ? [] : [U]): T {
		// @ts-ignore @townsquare-divergence: TS variance issue introduced by stricter conditional-type checks in newer tsc; remove if upstream RRR fixes the type or once TSQ catches up.
		return useHook(...args)[0];
	};
}

export const getRouterStore = (): StoreInstance<EntireRouterState, AllRouterActions> =>
	// @ts-ignore calling `getStore` without providing a scopeId
	defaultRegistry.getStore<EntireRouterState, AllRouterActions>(RouterStore);

// @ts-ignore accessing private store property
export const getRouterState = (): Readonly<EntireRouterState> =>
	getRouterStore().storeState.getState();
